import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { PracticeSignupService } from '../../services/practice-signup/practice-signup.service';
import { CVA_CONSTANTS } from '../../util/constants';
import { CVANotificationService } from '../../services/cva-notification/cva-notification.service';
import { NotificationClassEnum } from '../../models/notification-class.enum';
import { ROUTE_CONSTANTS } from '../../util/route.constants';
import { environment } from '../../../environments/environment';

@Component({
    standalone: false,
    selector: 'cva-practice-signup',
    templateUrl: './practice-signup.component.html',
    styleUrl: './practice-signup.component.scss',
    
})
export class PracticeSignupComponent implements OnInit {
  signupForm!: FormGroup;
  PRIVACY_POLICY_ROUTE = ROUTE_CONSTANTS.PRIVACY_POLICY;
  cvaUI_url: string = environment.cvaUI_url;
  formNotSubmitted = true;
  sucessMessage = CVA_CONSTANTS.MESSAGE.SAVED_PRACTISE_FORM;
  FIND_MY_FACILITY_ID_ROUTE = ROUTE_CONSTANTS.FIND_MY_FACILITY_ID;
  vendorList: string[] = ["TMG (Cloudland)", "Other"]

  constructor(private readonly fb: FormBuilder, private readonly practiceSignupService: PracticeSignupService,
    private readonly cvaNotificationService: CVANotificationService
  ) { }

  ngOnInit(): void {
    this.signupForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(65), Validators.pattern('^[a-zA-Z ]+$')]],
      email: ['', [Validators.required, Validators.maxLength(65), Validators.email]],
      phone: ['', [Validators.required, Validators.pattern('^\\+?[0-9]{7,15}$')]],
      practiceId: ['', [Validators.maxLength(15)]],
      practiceName: ['', [Validators.required]],
      clinicians: this.fb.array([
        this.createClinicianGroup()
      ]),
      consentPrivacy: [false, Validators.requiredTrue],
      consentUsage: [false, Validators.requiredTrue],
      signature: ['', [Validators.required, Validators.maxLength(65), Validators.pattern('^[a-zA-Z ]+$')]],
      itVendor: [''],
      itVendorContact: ['', Validators.maxLength(100)],
      enableACC: [true]
    });
    this.signupForm.get('itVendor')?.valueChanges.subscribe(value => {
    if (value !== 'Other') {
      this.signupForm.get('itVendorContact')?.reset();
    }
  });
  }

  createClinicianGroup(): FormGroup {
    return this.fb.group({
      hpi: ['', [Validators.required, Validators.maxLength(15), Validators.pattern('^[a-zA-Z0-9]+$')]],
      email: ['', [Validators.required, Validators.maxLength(65), Validators.email]],
      name: ['', [Validators.required, Validators.maxLength(65), Validators.pattern('^[a-zA-Z ]+$')]]
    });
  }

  get clinicians() {
    return this.signupForm.get('clinicians') as FormArray;
  }

  addClinician() {
    this.clinicians.push(this.createClinicianGroup());
  }

  removeClinician(index: number) {
    if (this.clinicians.length > 1) {
      this.clinicians.removeAt(index);
    } else {
      this.cvaNotificationService.showNotification(
        "At least one practitioner must be assigned.",
        NotificationClassEnum.ERROR
      );
    }
  }

  trimControl(controlName: string) {
    const control = this.signupForm.get(controlName);
    if (control && typeof control.value === 'string') {
      control.setValue(control.value.trim());
    }
  }

  trimNestedControl(index: number, controlName: string) {
    const group = this.clinicians.at(index) as FormGroup;
    const control = group.get(controlName);
    if (control && typeof control.value === 'string') {
      control.setValue(control.value.trim());
    }
  }

  onSubmit() {
    if (!this.signupForm.value.consentPrivacy || !this.signupForm.value.consentUsage) {
      return this.cvaNotificationService.showNotification(
        ' Your consent is required.',
        NotificationClassEnum.ERROR
      );
    }

    if (this.signupForm.valid) {
      const formData = this.signupForm.getRawValue();

      formData.additionalDetails = {
        itVendor: formData.itVendor,
        itVendorContact: formData.itVendorContact,
        enableACC: formData.enableACC
      };

      delete formData.itVendorContact;
      delete formData.enableACC;

      this.practiceSignupService.create(JSON.parse(JSON.stringify(formData)))
        .subscribe({
          next: (data) => {
            if (data?.uid) {
              this.formNotSubmitted = false;
            } else {
              this.cvaNotificationService.showNotification(
                CVA_CONSTANTS.MESSAGE.UNABLE_TO_PROCESS,
                NotificationClassEnum.ERROR
              );
            }
          },
          error: (e) => {
            const errorMessage = e?.error?.error?.errorMessage || CVA_CONSTANTS.MESSAGE.UNABLE_TO_PROCESS;
            this.cvaNotificationService.showNotification(
              errorMessage,
              NotificationClassEnum.ERROR
            );
          },
        });
    } else {
      this.signupForm.markAllAsTouched();
    }
  }
}
